import {Graph, Time, Timeout, TypeUtil} from 'yougile-util'
import {
  DataObjectId,
  DeadlineStickerValue,
  Hub,
  SandboxTree,
  shiftDeadlineKeepingLength
} from 'yougile-model'
import {Sync} from '@client/sync'
import {SyncEvents} from '@client/sync-events'
import {Gantt} from '@extensions/gantt'
import {globals} from '@extensions/globals-base'
import {stickerCtl} from '@extensions/sticker/sticker'
import {SubtaskUtil} from '@extensions/subtask-util'
import {Task} from '@extensions/task'

const draftTtl = 30 * Time.second
const staleCheckInterval = 10 * Time.second

interface DraftData {
  deadline: DeadlineStickerValue
}

interface HubDraft {
  timestamp: number
  original: DraftData
  draft: DraftData
}

export class GanttDraft {
  private static index: Map<DataObjectId, HubDraft> = new Map()
  private static staleCheckHandle: ReturnType<typeof setInterval> | undefined
  private static notifyHandle: Timeout | undefined
  private static syncListener: number
  private static companyListener: number
  private static subscribers: (() => void)[] = []

  public static getDraft(id: DataObjectId): DraftData | undefined {
    return this.index.get(id)?.draft
  }

  public static removeDraft(id: DataObjectId): void {
    this.index.delete(id)
    this.scheduleNotify()
  }

  public static init(): void {
    const initForCurrent = () => {
      this.removeSyncListener()
      this.addSyncListener()
    }

    initForCurrent()

    this.companyListener = globals.onCompanyChange(initForCurrent)

    this.staleCheckHandle && clearInterval(this.staleCheckHandle)
    this.staleCheckHandle = setInterval(
      () => this.staleCheck(),
      staleCheckInterval
    )
  }

  public static destroy(): void {
    this.removeSyncListener()
    globals.removeCompanyListener(this.companyListener)
  }

  public static subscribe(fn: () => void): () => void {
    if (this.subscribers.find(fn)) {
      this.subscribers = this.subscribers.filter(x => x !== fn)
    }

    this.subscribers.push(fn)

    return () => {
      this.subscribers = this.subscribers.filter(x => x !== fn)
    }
  }

  public static planDependentTasks(taskId: DataObjectId): void {
    const box = Sync.getCurrentSandbox()
    const hub = box.getHub(taskId)
    if (!hub || box.getType(hub) !== 'task') {
      return
    }

    const allDependent = this.getAllDependent(hub)
    const orderedDependent = Graph.topologicalSort(
      allDependent,
      x => Gantt.getLinksToHubs(x).filter(x => x.id !== hub.id),
      x => x.id
    )

    for (const dependent of orderedDependent) {
      this.planTaskByDependencies(dependent.id)
    }
  }

  public static planTaskByDependencies(taskId: DataObjectId): void {
    const box = Sync.getCurrentSandbox()
    const hub = box.getHub(taskId)
    if (
      !hub ||
      box.getType(hub) !== 'task' ||
      !Task.shouldAutoPlan(hub) ||
      box.isDeleted(hub.id) ||
      Task.isArchived(hub)
    ) {
      return
    }

    const tag = stickerCtl.Deadline.getSticker(box)
    const deadline =
      this.index.get(hub.id)?.draft.deadline ??
      (hub.stickers[tag.id] as DeadlineStickerValue | undefined)
    if (!deadline?.current?.deadline) {
      return
    }

    const maxFromEnd = this.findDeadlineByDependencies(hub.id)
    if (maxFromEnd <= 0) {
      return
    }

    const newDeadline = shiftDeadlineKeepingLength(
      deadline,
      maxFromEnd + Time.day,
      globals.currentUserId
    )

    if (newDeadline !== deadline) {
      const prev = this.index.get(hub.id)
      if (prev) {
        this.index.set(hub.id, {
          timestamp: Date.now(),
          original: prev.original,
          draft: {...prev, deadline: newDeadline}
        })
      } else {
        this.index.set(hub.id, {
          timestamp: Date.now(),
          original: {deadline},
          draft: {deadline: newDeadline}
        })
      }
      this.scheduleNotify()
    }
  }

  public static planEntitySubtree(
    entityId: DataObjectId,
    box: SandboxTree = Sync.getCurrentSandbox()
  ): void {
    const entity = box.get(entityId)
    const type = box.getType(entity)
    if (type === 'task') {
      GanttDraft.planTaskSubtree(entity.id)
    } else if (type === 'column') {
      GanttDraft.planColumnSubtree(entity.id)
    } else if (type === 'board') {
      GanttDraft.planBoardSubtree(entity.id)
    } else if (type === 'project') {
      GanttDraft.planProjectSubtree(entity.id)
    }
  }

  private static planProjectSubtree(projectId: DataObjectId): void {
    const box = Sync.getCurrentSandbox()
    const project = box.getHub(projectId)
    if (!project) {
      return
    }

    const boards = box.getChildrenHubs(project.id, {withDeleted: true})
    for (const board of boards) {
      this.planBoardSubtree(board.id)
    }
  }

  private static planBoardSubtree(boardId: DataObjectId): void {
    const box = Sync.getCurrentSandbox()
    const board = box.getHub(boardId)
    if (!board) {
      return
    }

    const columns = box.getChildrenHubs(board.id, {withDeleted: true})
    for (const column of columns) {
      this.planColumnSubtree(column.id)
    }
  }

  private static planColumnSubtree(columnId: DataObjectId): void {
    const box = Sync.getCurrentSandbox()
    const column = box.getHub(columnId)
    if (!column) {
      return
    }

    const tasks = box.getChildrenHubs(column.id, {withDeleted: true})
    for (const task of tasks) {
      this.planTaskSubtree(task.id)
    }
  }

  private static planTaskSubtree(taskId: DataObjectId): void {
    const box = Sync.getCurrentSandbox()
    const task = box.getHub(taskId)
    if (!task || box.getType(task) !== 'task') {
      return
    }

    const hubIds = this.getAllSubtaskIds(task.id)
    hubIds.push(task.id)

    for (const hubId of new Set(hubIds)) {
      this.planTaskByDependencies(hubId)
      this.planDependentTasks(hubId)
    }
  }

  private static findDeadlineByDependencies(taskId: DataObjectId): number {
    const box = Sync.getCurrentSandbox()
    const tag = stickerCtl.Deadline.getSticker(box)
    const toHub = box.getHub(taskId)
    if (!toHub) {
      return 0
    }
    const dependencies = toHub.data?.dependencies ?? {}

    let result = 0
    const linkedFromHubs = Gantt.getLinkedFromHubs(taskId)
    for (const hub of linkedFromHubs) {
      const deadline =
        this.index.get(hub.id)?.draft.deadline ??
        (hub.stickers?.[tag.id] as DeadlineStickerValue | undefined)

      if (!deadline?.current?.deadline) {
        continue
      }

      let linkData = dependencies[hub.id]
      if (typeof linkData !== 'object') {
        linkData = {}
      }
      const gap = linkData.gap ?? 0

      let deadlineDate = new Date(deadline.current.deadline)
      if (gap !== 0) {
        // Tasks ending on a weekend are treated as ending on a Fri before
        deadlineDate = Time.normalizeToWeekday(deadlineDate, false)
        deadlineDate = Time.addWorkingDays(deadlineDate, gap)
      }

      result = Math.max(result, deadlineDate.getTime())
    }
    return result
  }

  private static getAllDependent(hub: Hub): Hub[] {
    const result: Hub[] = []

    const observedIds = new Set<DataObjectId>()
    Graph.bfs({
      start: hub,
      enter: x => {
        if (observedIds.has(x.id)) {
          return false
        }
        if (x.id !== hub.id) {
          observedIds.add(x.id)
          result.push(x)
        }
        return true
      },
      adjacent: x => Gantt.getLinksToHubs(x)
    })

    return result
  }

  private static getAllSubtaskIds(taskId: DataObjectId): DataObjectId[] {
    const box = Sync.getCurrentSandbox()
    const hub = box.getHub(taskId)
    if (!hub) {
      return []
    }

    const result = new Set<DataObjectId>()

    Graph.bfs({
      start: hub,
      adjacent: task =>
        SubtaskUtil.getSubtaskIds(task)
          .map(id => box.getHub(id))
          .filter(TypeUtil.isTruthy),
      enter: x => {
        if (result.has(x.id)) {
          return false
        }
        result.add(x.id)
        return true
      }
    })

    return Array.from(result)
  }

  private static addSyncListener(): void {
    this.syncListener = SyncEvents.addListener(
      globals.getCompanyId(),
      '*',
      res => {
        for (const {companyId, ids} of res) {
          const box = Sync.get({companyId})
          for (const id of ids) {
            const hub = box.getHub(id)
            if (hub && box.getType(hub) === 'task' && this.isDraftStale(hub)) {
              this.removeDraft(hub.id)
            }
          }
        }
      },
      true
    )
  }

  private static isDraftStale(hub: Hub): boolean {
    const hubDraft = this.index.get(hub.id)
    if (!hubDraft) {
      return false
    }

    const box = Sync.getCurrentSandbox()
    const tag = stickerCtl.Deadline.getSticker(box)

    const currentDeadline = hub.stickers[tag.id] as
      | DeadlineStickerValue
      | undefined
    const originalDeadline = hubDraft.original.deadline

    if (
      currentDeadline?.current?.startDate ===
        originalDeadline?.current?.startDate &&
      currentDeadline?.current?.deadline === originalDeadline?.current?.deadline
    ) {
      return false
    }

    return true
  }

  private static removeSyncListener(): void {
    this.syncListener && SyncEvents.removeListener(this.syncListener)
  }

  private static scheduleNotify(): void {
    if (!this.notifyHandle) {
      this.notifyHandle = setTimeout(() => {
        this.notifyHandle = undefined
        for (const fn of this.subscribers) {
          fn()
        }
      })
    }
  }

  private static staleCheck(): void {
    const now = Date.now()
    const allKeys = Array.from(this.index.keys())
    for (const key of allKeys) {
      const value = this.index.get(key)
      if (value && now - value.timestamp > draftTtl) {
        if (this.index.delete(key)) {
          this.scheduleNotify()
        }
      }
    }
  }
}
